import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const EMPTY_FORM = {
  username: "",
  first_name: "",
  last_name: "",
  phone_no: "",
  designation: "",
  address: "",
  email: "",
  password: "",
};

const FIELD_LABELS = {
  username: "Username",
  first_name: "First name",
  last_name: "Last name",
  phone_no: "Phone number",
  designation: "Designation",
  address: "Address",
  email: "Email",
  password: "Password",
};

const optionLabel = (user) =>
  `${user.designation} — ${user.first_name || ""} ${user.last_name || ""} (${user._id})`.trim();

const CustomUserCrudLayer = () => {
  const apiBaseUrl = process.env.REACT_APP_BE_API_URL;
  const [form, setForm] = useState(EMPTY_FORM);
  const [hierarchy, setHierarchy] = useState([{ level: 1, designation: "", user_id: "", search: "" }]);
  const [dfoUsers, setDfoUsers] = useState([]);
  const [customUsers, setCustomUsers] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authConfig = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  }), []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [optionsResponse, usersResponse] = await Promise.all([
        axios.get(`${apiBaseUrl}/backend/users/custom/dfo-options`, authConfig),
        axios.get(`${apiBaseUrl}/backend/users/custom`, authConfig),
      ]);
      setDfoUsers(optionsResponse.data?.data || []);
      setCustomUsers(usersResponse.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load custom users.");
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, authConfig]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setHierarchy([{ level: 1, designation: "", user_id: "", search: "" }]);
    setEditingId("");
  };

  const handleHierarchySearch = (index, value) => {
    const selected = dfoUsers.find((user) => optionLabel(user) === value);
    setHierarchy((current) => current.map((item, itemIndex) => itemIndex === index ? {
      ...item,
      search: value,
      user_id: selected?._id || "",
      designation: selected?.designation || "",
    } : item));
  };

  const addLevel = () => {
    setHierarchy((current) => [...current, {
      level: current.length + 1,
      designation: "",
      user_id: "",
      search: "",
    }]);
  };

  const removeLevel = (index) => {
    setHierarchy((current) => current
      .filter((_, itemIndex) => itemIndex !== index)
      .map((item, itemIndex) => ({ ...item, level: itemIndex + 1 })));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (Object.entries(form).some(([key, value]) => key !== "password" && !value.trim())) {
      toast.error("Please fill all user fields.");
      return;
    }
    if (!editingId && !form.password) {
      toast.error("Password is required for a new user.");
      return;
    }
    if (hierarchy.some((item) => !item.user_id)) {
      toast.error("Select a DFO for every hierarchy level.");
      return;
    }

    const payload = {
      ...form,
      upper_hirarchy: hierarchy.map(({ level, designation, user_id }) => ({ level, designation, user_id })),
    };
    if (editingId && !payload.password) delete payload.password;

    try {
      setSaving(true);
      if (editingId) {
        await axios.put(`${apiBaseUrl}/backend/users/custom/${editingId}`, payload, authConfig);
        toast.success("Custom user updated successfully.");
      } else {
        await axios.post(`${apiBaseUrl}/backend/users/custom`, payload, authConfig);
        toast.success("Custom user created successfully.");
      }
      resetForm();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save custom user.");
    } finally {
      setSaving(false);
    }
  };

  const editUser = (user) => {
    setEditingId(user._id);
    setForm(Object.keys(EMPTY_FORM).reduce((result, key) => ({
      ...result,
      [key]: key === "password" ? "" : user[key] || "",
    }), {}));
    setHierarchy(user.upper_hirarchy.map((item, index) => {
      const selected = item.user_id;
      return {
        level: index + 1,
        designation: item.designation,
        user_id: selected?._id || selected,
        search: selected?._id ? optionLabel(selected) : `${item.designation} (${selected})`,
      };
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.first_name} ${user.last_name}?`)) return;
    try {
      await axios.patch(`${apiBaseUrl}/backend/users/custom/delete/${user._id}`, {}, authConfig);
      toast.success("Custom user deleted successfully.");
      if (editingId === user._id) resetForm();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete custom user.");
    }
  };

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card">
          <div className="card-header"><h5 className="mb-0">{editingId ? "Edit Custom User" : "Add Custom User"}</h5></div>
          <div className="card-body">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="row gy-3">
                {Object.keys(EMPTY_FORM).map((field) => (
                  <div className={field === "address" ? "col-12" : "col-md-6"} key={field}>
                    <label className="form-label" htmlFor={`custom-${field}`}>{FIELD_LABELS[field]}{editingId && field === "password" ? " (leave blank to keep it)" : " *"}</label>
                    {field === "address" ? (
                      <textarea id={`custom-${field}`} className="form-control" required value={form[field]} onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))} />
                    ) : (
                      <input id={`custom-${field}`} className="form-control" type={field === "password" ? "password" : field === "email" ? "email" : "text"} autoComplete={field === "password" ? "new-password" : "off"} required={!editingId || field !== "password"} value={form[field]} onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))} />
                    )}
                  </div>
                ))}
              </div>

              <hr className="my-4" />
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div><h6 className="mb-1">Upper hierarchy</h6><small className="text-muted">Search by designation, name, or user ID.</small></div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLevel}><Icon icon="mdi:plus" /> Add level</button>
              </div>
              <datalist id="dfo-options">
                {dfoUsers.filter((user) => user._id !== editingId).map((user) => <option key={user._id} value={optionLabel(user)} />)}
              </datalist>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {hierarchy.map((item, index) => (
                  <React.Fragment key={item.level}>
                    {index > 0 ? <Icon icon="mdi:arrow-right" className="text-primary text-2xl" aria-hidden="true" /> : null}
                    <div className="border rounded p-3 flex-grow-1" style={{ minWidth: 260 }}>
                      <div className="d-flex justify-content-between mb-2">
                        <strong>Level {item.level}</strong>
                        {hierarchy.length > 1 ? <button type="button" className="btn btn-sm text-danger p-0" aria-label={`Remove level ${item.level}`} onClick={() => removeLevel(index)}><Icon icon="mdi:close" /></button> : null}
                      </div>
                      <input className="form-control" list="dfo-options" placeholder="Search by designation, name, or user ID" value={item.search} onChange={(event) => handleHierarchySearch(index, event.target.value)} />
                      <small className="d-block mt-2 text-muted">User ID: {item.user_id || "Not selected"}</small>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className="mt-4 p-3 bg-light rounded">
                <h6>Hierarchy tree preview</h6>
                {hierarchy.map((item, index) => (
                  <div key={item.level} className="text-center">
                    {index > 0 ? <Icon icon="mdi:arrow-down" className="d-block mx-auto my-1 text-primary" /> : null}
                    <span className={`badge ${item.user_id ? "bg-primary" : "bg-secondary"} px-3 py-2`}>Level {item.level}: {item.designation || "Not assigned"}</span>
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4">
                {editingId ? <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel edit</button> : null}
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : editingId ? "Update user" : "Create user"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="card">
          <div className="card-header"><h5 className="mb-0">Custom Users</h5></div>
          <div className="card-body table-responsive">
            <table className="table bordered-table mb-0">
              <thead><tr><th>Name</th><th>Username</th><th>Designation</th><th>Email</th><th>Hierarchy</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="6" className="text-center">Loading...</td></tr> : customUsers.length === 0 ? <tr><td colSpan="6" className="text-center">No custom users found.</td></tr> : customUsers.map((user) => (
                  <tr key={user._id}>
                    <td>{user.first_name} {user.last_name}</td><td>{user.username}</td><td>{user.designation}</td><td>{user.email}</td>
                    <td>{user.upper_hirarchy.map((item) => `L${item.level}: ${item.designation}`).join(" → ")}</td>
                    <td><div className="d-flex gap-2"><button type="button" className="btn btn-sm btn-outline-success" onClick={() => editUser(user)}>Edit</button><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteUser(user)}>Delete</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomUserCrudLayer;
